import type { ContentAssetEntry, ContentAssetFamily, ContentAssetRegistryList, ContentAssetsRegistryData } from '../registrySchemas/contentAssets.js';
import type { SkillEntry } from '../registrySchemas/skills.js';
import type { TemplateName } from './assetType.js';

import { CONTENT_ASSET_REGISTRY_LISTS, ContentAssetsRegistry, mapRegistryLists } from '../registrySchemas/contentAssets.js';
import { SkillsRegistry } from '../registrySchemas/skills.js';
import { SlugListRegistry } from '../registrySchemas/slugList.js';

/** One template's allowed slugs in each registry list, plus their union. */
export interface ContentAssetTypeSets extends Record<ContentAssetRegistryList, ReadonlySet<string>> {
  /** Each character by slug, with the description Phoenix exported beside it. */
  charactersBySlug: ReadonlyMap<string, ContentAssetEntry>;
  /** The union of every list — every slug allowed for this template. */
  all: ReadonlySet<string>;
}

/**
 * The content-assets registry, indexed by template name. Every kind comes from
 * the flat exported lists, each entry placed under every template its own
 * `templates` names; `dil` takes story's entries of a kind when none names it.
 * `all` is the union across every template, plus every other image whatever
 * templates it names — a card picture only has to exist, and one uploaded from
 * the Studio names none. `families` is each kind's definition by kind name, as
 * Phoenix exported it.
 */
export interface ContentAssetSets {
  story: ContentAssetTypeSets;
  dil: ContentAssetTypeSets;
  lesson: ContentAssetTypeSets;
  gameshow: ContentAssetTypeSets;
  all: ReadonlySet<string>;
  families: ReadonlyMap<string, ContentAssetFamily>;
}

/**
 * Raw registry data, as plain JSON-shaped values. This is the wire shape:
 * callers (Phoenix, hydra, the node adapter, tests) pass the data itself —
 * never file paths. Every registry a rule resolves against is REQUIRED; there
 * is no unconfigured state for those, so no rule skips silently for want of
 * one. `careerClusters` is the exception — see below.
 */
export interface RegistryData {
  /** Flat object keyed by skill slug → entry. */
  skills: Record<string, SkillEntry>;
  /**
   * Flat array of career-cluster slugs — optional, and normally absent.
   *
   * The other registries police slugs Phoenix RESOLVES on import: an unknown
   * skill or consideration area silently drops a relation, which is what the
   * validator is there to catch. `indexing.careerCluster` resolves to nothing
   * — Phoenix stores it in a jsonb blob and title-cases it for display — so
   * there is no owning table to be the vocabulary, and nothing breaks when it
   * drifts. Supply a list only if someone takes ownership of that taxonomy;
   * the membership check turns itself on when one is here.
   */
  careerClusters?: string[];
  /** Flat array of consideration-area slugs. */
  considerationAreas: string[];
  /** One flat exported list per kind (see `CONTENT_ASSET_REGISTRY_LIST`) and the families they were described against. */
  contentAssets: ContentAssetsRegistryData;
}

/** Built lookup structures the rules read from `ctx.registries`. */
export interface Registries {
  skills: ReadonlyMap<string, SkillEntry>;
  /** Absent unless a career-clusters list was supplied; the cluster check is skipped when it is. */
  careerClusters?: ReadonlySet<string>;
  considerationAreas: ReadonlySet<string>;
  contentAssets: ContentAssetSets;
}

/** Thrown when registry data does not match its schema. */
export class RegistryError extends Error {
  constructor(registry: string, detail: string) {
    super(`Invalid ${registry} registry: ${detail}`);
    this.name = 'RegistryError';
  }
}

const firstIssueDetail = (error: { issues: { path: PropertyKey[]; message: string }[] }): string => {
  const first = error.issues[0];
  const where = first?.path.map(String).join('.') ?? '';
  return `${where === '' ? '(root)' : where} — ${first?.message ?? 'unknown error'}`;
};

const naming = (template: TemplateName, entries: ContentAssetEntry[]): ContentAssetEntry[] =>
  entries.filter(entry => entry.templates.includes(template));

/**
 * The entries a template may use. Day-in-life is story's sibling: when no entry of a kind
 * names `dil`, it takes the ones that name `story`, so a list curated for stories serves
 * both until someone says otherwise.
 */
const placedUnder = (template: TemplateName, entries: ContentAssetEntry[]): ContentAssetEntry[] => {
  const own = naming(template, entries);
  return template === 'dil' && own.length === 0 ? naming('story', entries) : own;
};

const toSets = (template: TemplateName, registry: ContentAssetsRegistry, all: Set<string>): ContentAssetTypeSets => {
  const lists = mapRegistryLists(list => new Set(placedUnder(template, registry[list]).map(entry => entry.slug)));
  const allowed = new Set(CONTENT_ASSET_REGISTRY_LISTS.flatMap(list => [...lists[list]]));
  for (const s of allowed) {
    all.add(s);
  }
  const charactersBySlug = new Map(placedUnder(template, registry.characters).map(entry => [entry.slug, entry]));
  return { ...lists, charactersBySlug, all: allowed };
};

/**
 * Validates raw registry data and builds the lookup structures rules consume.
 * Every registry a rule resolves against is required — passing partial data is
 * a type error and invalid data throws a `RegistryError` — so no rule skips
 * silently for want of one. `careerClusters` is the exception: absent is a
 * legitimate state, and its membership check stands down.
 */
export const buildRegistries = (data: RegistryData): Registries => {
  const skills = SkillsRegistry.safeParse(data.skills);
  if (!skills.success) {
    throw new RegistryError('skills', firstIssueDetail(skills.error));
  }

  // Supplied or not — but a supplied one still has to be a slug list.
  const careerClusters = data.careerClusters === undefined ? null : SlugListRegistry.safeParse(data.careerClusters);
  if (careerClusters !== null && !careerClusters.success) {
    throw new RegistryError('career-clusters', firstIssueDetail(careerClusters.error));
  }

  const considerationAreas = SlugListRegistry.safeParse(data.considerationAreas);
  if (!considerationAreas.success) {
    throw new RegistryError('consideration-areas', firstIssueDetail(considerationAreas.error));
  }

  const contentAssets = ContentAssetsRegistry.safeParse(data.contentAssets);
  if (!contentAssets.success) {
    throw new RegistryError('content-assets', firstIssueDetail(contentAssets.error));
  }

  const all = new Set<string>(contentAssets.data.others.map(entry => entry.slug));
  return {
    skills: new Map(Object.entries(skills.data)),
    ...(careerClusters?.success ? { careerClusters: new Set(careerClusters.data) } : {}),
    considerationAreas: new Set(considerationAreas.data),
    contentAssets: {
      story: toSets('story', contentAssets.data, all),
      dil: toSets('dil', contentAssets.data, all),
      lesson: toSets('lesson', contentAssets.data, all),
      gameshow: toSets('gameshow', contentAssets.data, all),
      all,
      families: new Map(contentAssets.data.families.map(family => [family.kind, family])),
    },
  };
};
